import { _breakP, debounce } from '../common/js/lib/utils.js';

/**
 * メインビジュアル動画（MainVisualMovie）
 *
 * 【目的】
 * トップのメインビジュアルで、PC/SP 別の video を切り替えつつ再生する。
 * 静止画ポスター層の表示制御、任意の操作ボタン、スクロールに連動したパララックスを行う。
 *
 * 【必須 DOM】
 * - ルート: `.js-mainVisualMovie`
 * - 内側（パララックス用）: `.js-mainVisualMovieInner`
 * - ポスター（静止画オーバーレイ）: `.js-mainVisualMoviePoster` … 表示切替に `is-hidden`
 * - 動画: `#mainVisualMovieVideoPC` / `#mainVisualMovieVideoSP`（どちらか一方のみでも可）
 *
 * 【ブレークポイント】
 * - `utils._breakP` と SCSS の `$breakP: 768px` に合わせる。
 * - `_breakP.SP`（max-width: 767px）のとき SP 用動画、それ以外は PC 用動画をアクティブとする。
 *
 * 【動画まわり】
 * - 初期は `preload="none"` にし、表示側の動画のみ `preload="auto"` と `load()` で読み込む。
 * - `src` が空の動画は再生せずポスターのみ表示する。
 * - 再生中はアクティブに `is-start`、非アクティブに `is-hidden`。ポスターは `is-hidden` で非表示。
 * - `playing` で `poster` 属性を除去する（表示まわりの不整合回避）。
 * - 正常終了／スキップ後は、アクティブ動画を `is-ended` で最終フレーム付近までシークした静止状態にする。フォールバック用ポスター層は出さない。
 * - `src` が空などアクティブが再生不能で終わった場合はこれまでどおりポスター層を表示する。
 * - 読み込みエラー確定後は全動画を隠し、ポスター層のみ表示する（`errorPosterDelayMs`）。操作ボタン類は全非表示・フォーカス外し。
 *
 * 【prefers-reduced-motion】
 * - 有効時は動画を止め、ポスター層を表示し、パララックスを解除する（アクセシビリティ用の既定・エラーだけに限定しない）。
 * - 設定変更時に状態を切り替える。
 *
 * 【リサイズ】
 * - `resizeDebounceMs` でデバウンスし、PC/SP の切替・非アクティブの pause・必要に応じ再再生する。
 * - 視聴完了後はブレークポイント変更時もポスターに切り替えず、現ブレークの動画を終端付近までシークして `is-ended` を再適用する。
 *
 * 【パララックス】
 * - `parallaxRatio` で縦移動、`parallaxHideScroll` × ビューポート高さを超えたスクロールでルートに `is-hidden`。
 * - reduced motion 時はパララックスしない。
 *
 * 【任意の操作 UI（マークアップにあれば紐づく）】
 * - `.js-mainVisualMovieSkip` … スキップ（終了扱い）
 * - `.js-mainVisualMovieReplay` … 再再生
 * - `.js-mainVisualMovieStop` / `.js-mainVisualMoviePlay` … 一時停止 / 再開（表示の切替）
 * - 再生開始時・終了時に `onStartFunc` / `onCompleteFunc` で `is-hidden` と `tabindex` を切替。エラー完了時のみ `hideAllMvControlButtons` で全ボタンを隠す。
 *
 * 【インスタンス定数（constructor 内）】
 * - `resizeDebounceMs`, `errorPosterDelayMs`, `playUiDelayMs`, `seekEndEpsilon`, `parallaxRatio`, `parallaxHideScroll`
 *
 * 【破棄】
 * - `destroy()` でイベントを解除する。
 */
export default class MainVisualMovie {
  constructor() {
    this.$mainVisualMovie = document.querySelector('.js-mainVisualMovie');
    if (!this.$mainVisualMovie) return;

    this.$mainVisualMovieInner = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMovieInner'
    );
    this.$mainVisualMoviePoster = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMoviePoster'
    );
    this.$mainVisualMovieVideoPC = document.getElementById(
      'mainVisualMovieVideoPC'
    );
    this.$mainVisualMovieVideoSP = document.getElementById(
      'mainVisualMovieVideoSP'
    );
    this.mainVisualMovieCap = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMovieCap'
    );

    if (!this.$mainVisualMovieVideoPC && !this.$mainVisualMovieVideoSP) return;

    this.resizeDebounceMs = 150;
    this.errorPosterDelayMs = 500;
    this.playUiDelayMs = 100;
    this.parallaxRatio = 0.3;
    this.parallaxHideScroll = 1.2;
    this.seekEndEpsilon = 0.033;

    this.$skipBtn = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMovieSkip'
    );
    this.$replayBtn = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMovieReplay'
    );
    this.$stopBtn = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMovieStop'
    );
    this.$playBtn = this.$mainVisualMovie.querySelector(
      '.js-mainVisualMoviePlay'
    );

    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    this.respectReducedMotion = false;

    this.isPlayAttemptPending = false;
    this.currentIsPC = !_breakP.SP;
    this.isVideoEnded = false;
    this.hasVideoError = false;
    this.errorTimeoutId = null;
    this.rafId = null;
    this.boundScrollHandler = null;

    this.allVideos = [
      this.$mainVisualMovieVideoPC,
      this.$mainVisualMovieVideoSP
    ].filter(Boolean);

    this.boundHandleResize = debounce(
      () => this.handleResize(),
      this.resizeDebounceMs
    );
    this.boundHandleMotionChange = () => this.handleMotionPreferenceChange();
    this.onPlaying = () => this.handlePlaying();
    this.onEnded = () => this.handleEnded();
    this.onVideoError = e => this.handleVideoError(e);
    this.boundSkipClick = () => this.handleSkipClick();
    this.boundReplayClick = () => this.handleReplayClick();
    this.boundStopClick = () => this.handleStopClick();
    this.boundPlayClick = () => this.handlePlayClick();

    this.init();
  }

  get isReducedMotion() {
    return this.respectReducedMotion && this.prefersReducedMotion.matches;
  }

  setRespectReducedMotion(value) {
    this.respectReducedMotion = value;
    this.handleMotionPreferenceChange();
  }

  init() {
    this.allVideos.forEach(v => {
      v.preload = 'none';
      v.removeAttribute('autoplay');
    });

    this.allVideos.forEach(v => {
      v.addEventListener('error', this.onVideoError);
      v.addEventListener('playing', this.onPlaying);
      v.addEventListener('ended', this.onEnded);
    });

    if (this.$skipBtn) {
      this.$skipBtn.addEventListener('click', this.boundSkipClick);
    }
    if (this.$replayBtn) {
      this.$replayBtn.addEventListener('click', this.boundReplayClick);
    }
    if (this.$stopBtn) {
      this.$stopBtn.addEventListener('click', this.boundStopClick);
    }
    if (this.$playBtn) {
      this.$playBtn.addEventListener('click', this.boundPlayClick);
    }

    if (this.isReducedMotion) {
      this.pauseAllVideos();
      this.clearVideoLayerClasses();
      this.showPoster();
    } else {
      this.handleResize();
    }

    window.addEventListener('resize', this.boundHandleResize);
    this.prefersReducedMotion.addEventListener(
      'change',
      this.boundHandleMotionChange
    );
    this.initParallax();
  }

  hasPlayableSource(video) {
    if (!video) return false;
    const attr = video.getAttribute('src');
    return Boolean(attr && attr.trim() !== '');
  }

  getCurrentVideo() {
    return _breakP.SP
      ? this.$mainVisualMovieVideoSP
      : this.$mainVisualMovieVideoPC;
  }

  showPoster() {
    if (this.$mainVisualMoviePoster) {
      this.$mainVisualMoviePoster.classList.remove('is-hidden');
    }
  }

  hidePoster() {
    if (this.$mainVisualMoviePoster) {
      this.$mainVisualMoviePoster.classList.add('is-hidden');
    }
  }

  onStartFunc() {
    if (this.$skipBtn) {
      this.$skipBtn.classList.remove('is-hidden');
      this.$skipBtn.removeAttribute('tabindex');
    }
    if (this.$replayBtn) {
      this.$replayBtn.classList.add('is-hidden');
      this.$replayBtn.setAttribute('tabindex', '-1');
    }
    if (this.$stopBtn) {
      this.$stopBtn.classList.remove('is-hidden');
      this.$stopBtn.removeAttribute('tabindex');
    }
    if (this.$playBtn) {
      this.$playBtn.classList.add('is-hidden');
      this.$playBtn.setAttribute('tabindex', '-1');
    }
  }

  onCompleteFunc() {
    if (this.$skipBtn) {
      this.$skipBtn.classList.add('is-hidden');
      this.$skipBtn.setAttribute('tabindex', '-1');
    }
    if (this.$replayBtn) {
      this.$replayBtn.classList.remove('is-hidden');
      this.$replayBtn.removeAttribute('tabindex');
    }
    if (this.$stopBtn) {
      this.$stopBtn.classList.add('is-hidden');
      this.$stopBtn.setAttribute('tabindex', '-1');
    }
    if (this.$playBtn) {
      this.$playBtn.classList.add('is-hidden');
      this.$playBtn.setAttribute('tabindex', '-1');
    }
    if (this.mainVisualMovieCap) {
      this.mainVisualMovieCap.classList.add('is-ended');
    }
  }

  hideAllMvControlButtons() {
    if (this.$skipBtn) {
      this.$skipBtn.classList.add('is-hidden');
      this.$skipBtn.setAttribute('tabindex', '-1');
    }
    if (this.$replayBtn) {
      this.$replayBtn.classList.add('is-hidden');
      this.$replayBtn.setAttribute('tabindex', '-1');
    }
    if (this.$stopBtn) {
      this.$stopBtn.classList.add('is-hidden');
      this.$stopBtn.setAttribute('tabindex', '-1');
    }
    if (this.$playBtn) {
      this.$playBtn.classList.add('is-hidden');
      this.$playBtn.setAttribute('tabindex', '-1');
    }
  }

  clearErrorTimeout() {
    if (this.errorTimeoutId != null) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }
  }

  pauseAllVideos() {
    this.allVideos.forEach(v => v.pause());
  }

  clearVideoLayerClasses() {
    this.allVideos.forEach(v => {
      v.classList.remove('is-start', 'is-ended', 'is-hidden');
    });
  }

  syncEndedLayers() {
    const active = this.getCurrentVideo();
    this.allVideos.forEach(v => {
      v.classList.remove('is-start');
      if (v === active && active && this.hasPlayableSource(active)) {
        v.classList.remove('is-hidden');
        v.classList.add('is-ended');
      } else {
        v.classList.remove('is-ended');
        v.classList.add('is-hidden');
      }
    });
  }

  /**
   * @param {HTMLVideoElement | null | undefined} video
   * @param {(() => void) | undefined} onDone
   */
  seekVideoNearEnd(video, onDone) {
    if (!video || !this.hasPlayableSource(video)) {
      onDone?.();
      return;
    }

    let finished = false;
    let fallbackId = null;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (fallbackId != null) {
        clearTimeout(fallbackId);
        fallbackId = null;
      }
      video.pause();
      onDone?.();
    };

    fallbackId = window.setTimeout(() => finish(), 800);

    const runSeek = () => {
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) {
        finish();
        return;
      }
      const target = Math.max(0, dur - this.seekEndEpsilon);
      video.addEventListener('seeked', () => finish(), { once: true });
      try {
        video.currentTime = target;
      } catch {
        finish();
      }
    };

    if (
      video.readyState >= HTMLMediaElement.HAVE_METADATA &&
      Number.isFinite(video.duration) &&
      video.duration > 0
    ) {
      runSeek();
    } else {
      video.addEventListener(
        'loadedmetadata',
        () => {
          runSeek();
        },
        { once: true }
      );
    }
  }

  syncVideoLayerOnPlaying() {
    if (this.isVideoEnded || this.hasVideoError) return;
    this.hidePoster();
    const active = this.getCurrentVideo();
    this.allVideos.forEach(v => {
      if (v === active) {
        v.classList.remove('is-hidden', 'is-ended');
        v.classList.add('is-start');
      } else {
        v.classList.remove('is-start', 'is-ended');
        v.classList.add('is-hidden');
      }
    });
    window.setTimeout(() => {
      this.onStartFunc();
    }, this.playUiDelayMs);
  }

  playVideo(video) {
    if (!video || this.isPlayAttemptPending) return;

    if (video.readyState >= 2) {
      video.play().catch(() => this.handleVideoError({ target: video }));
    } else {
      this.isPlayAttemptPending = true;
      const onCanPlay = () => {
        this.isPlayAttemptPending = false;
        video.play().catch(() => this.handleVideoError({ target: video }));
      };
      video.addEventListener('canplay', onCanPlay, { once: true });
    }
  }

  handleVideoEnd() {
    if (this.isVideoEnded) return;
    this.clearErrorTimeout();
    this.isVideoEnded = true;
    this.pauseAllVideos();

    const active = this.getCurrentVideo();
    if (!active || !this.hasPlayableSource(active)) {
      this.allVideos.forEach(v => {
        v.classList.remove('is-start', 'is-ended');
        v.classList.add('is-hidden');
      });
      this.showPoster();
      this.onCompleteFunc();
      return;
    }

    this.hidePoster();
    this.syncEndedLayers();
    this.onCompleteFunc();
  }

  handleVideoError(event) {
    if (this.isReducedMotion) return;
    if (this.hasVideoError) return;
    if (this.isVideoEnded) return;

    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video !== this.getCurrentVideo()) return;

    this.clearErrorTimeout();
    this.errorTimeoutId = window.setTimeout(() => {
      this.errorTimeoutId = null;
      this.hasVideoError = true;
      this.pauseAllVideos();
      this.allVideos.forEach(v => {
        v.classList.remove('is-start', 'is-ended');
        v.classList.add('is-hidden');
      });
      this.showPoster();
      this.hideAllMvControlButtons();
    }, this.errorPosterDelayMs);
  }

  handleResize() {
    if (this.isReducedMotion) return;

    const isPC = !_breakP.SP;
    const activeVideo = isPC
      ? this.$mainVisualMovieVideoPC
      : this.$mainVisualMovieVideoSP;
    const inactiveVideo = isPC
      ? this.$mainVisualMovieVideoSP
      : this.$mainVisualMovieVideoPC;

    const viewportChanged = this.currentIsPC !== isPC;
    this.currentIsPC = isPC;

    if (viewportChanged) {
      if (this.isVideoEnded && !this.hasVideoError) {
        this.hidePoster();
        if (this.hasPlayableSource(activeVideo)) {
          if (activeVideo.preload === 'none') {
            activeVideo.preload = 'auto';
            activeVideo.load();
          }
          this.seekVideoNearEnd(activeVideo, () => {
            this.syncEndedLayers();
          });
        } else {
          this.showPoster();
        }
      } else {
        this.showPoster();
      }
    }

    if (inactiveVideo) {
      inactiveVideo.pause();
    }

    if (!this.hasPlayableSource(activeVideo)) {
      this.showPoster();
      return;
    }

    if (activeVideo && !this.isVideoEnded && !this.hasVideoError) {
      if (activeVideo.preload === 'none') {
        activeVideo.preload = 'auto';
        activeVideo.load();
      }
      this.playVideo(activeVideo);
    }

    if (
      activeVideo &&
      this.isVideoEnded &&
      !this.hasVideoError &&
      !viewportChanged
    ) {
      this.syncEndedLayers();
    }
  }

  handleMotionPreferenceChange() {
    if (this.isReducedMotion) {
      this.pauseAllVideos();
      this.clearVideoLayerClasses();
      this.showPoster();
      this.resetParallax();
    } else {
      this.hasVideoError = false;
      this.isVideoEnded = false;
      this.clearVideoLayerClasses();
      this.handleResize();
      this.initParallax();
    }
  }

  resetParallax() {
    if (this.boundScrollHandler) {
      window.removeEventListener('scroll', this.boundScrollHandler);
      this.boundScrollHandler = null;
    }
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.$mainVisualMovieInner) {
      this.$mainVisualMovieInner.style.transform = '';
    }
    this.$mainVisualMovie.classList.remove('is-hidden');
  }

  initParallax() {
    if (!this.$mainVisualMovieInner) return;
    if (this.isReducedMotion) return;
    if (this.boundScrollHandler) return;

    const hideThreshold = window.innerHeight * this.parallaxHideScroll;
    let ticking = false;

    this.boundScrollHandler = () => {
      if (ticking) return;
      ticking = true;
      this.rafId = requestAnimationFrame(() => {
        const scrollableDistance = window.innerHeight;
        const scrollY = window.scrollY;

        if (scrollY > hideThreshold) {
          this.$mainVisualMovie.classList.add('is-hidden');
        } else {
          this.$mainVisualMovie.classList.remove('is-hidden');
        }

        if (scrollY <= scrollableDistance) {
          const yMove = scrollY * this.parallaxRatio;
          this.$mainVisualMovieInner.style.transform = `translateY(${-yMove}px)`;
        } else {
          this.$mainVisualMovieInner.style.transform = `translateY(${-scrollableDistance * this.parallaxRatio}px)`;
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', this.boundScrollHandler, {
      passive: true
    });
    this.boundScrollHandler();
  }

  handlePlaying() {
    this.clearErrorTimeout();
    if (this.isVideoEnded || this.hasVideoError) return;
    if (this.isReducedMotion) return;

    const v = this.getCurrentVideo();
    if (v) {
      v.removeAttribute('poster');
    }
    this.syncVideoLayerOnPlaying();
  }

  handleEnded() {
    if (this.isVideoEnded || this.hasVideoError) return;
    this.handleVideoEnd();
  }

  handleSkipClick() {
    const currentVideo = this.getCurrentVideo();
    if (
      !currentVideo ||
      !this.hasPlayableSource(currentVideo) ||
      this.isVideoEnded ||
      this.hasVideoError
    ) {
      return;
    }
    this.seekVideoNearEnd(currentVideo, () => {
      this.handleVideoEnd();
    });
  }

  handleReplayClick() {
    this.hasVideoError = false;
    this.isVideoEnded = false;
    this.clearErrorTimeout();
    this.clearVideoLayerClasses();
    this.hidePoster();
    const currentVideo = this.getCurrentVideo();
    if (!currentVideo || !this.hasPlayableSource(currentVideo)) {
      this.showPoster();
      return;
    }
    currentVideo.removeAttribute('poster');
    currentVideo.currentTime = 0;
    if (currentVideo.preload === 'none') {
      currentVideo.preload = 'auto';
      currentVideo.load();
    }
    this.playVideo(currentVideo);
  }

  handleStopClick() {
    const currentVideo = this.getCurrentVideo();
    if (currentVideo && !currentVideo.paused) {
      currentVideo.pause();
      if (this.$stopBtn) {
        this.$stopBtn.classList.add('is-hidden');
        this.$stopBtn.setAttribute('tabindex', '-1');
      }
      if (this.$playBtn) {
        this.$playBtn.classList.remove('is-hidden');
        this.$playBtn.removeAttribute('tabindex');
      }
    }
  }

  handlePlayClick() {
    const currentVideo = this.getCurrentVideo();
    if (currentVideo && currentVideo.paused) {
      currentVideo
        .play()
        .catch(() => this.handleVideoError({ target: currentVideo }));
      if (this.$playBtn) {
        this.$playBtn.classList.add('is-hidden');
        this.$playBtn.setAttribute('tabindex', '-1');
      }
      if (this.$stopBtn) {
        this.$stopBtn.classList.remove('is-hidden');
        this.$stopBtn.removeAttribute('tabindex');
      }
    }
  }

  destroy() {
    window.removeEventListener('resize', this.boundHandleResize);
    this.prefersReducedMotion?.removeEventListener(
      'change',
      this.boundHandleMotionChange
    );
    this.allVideos.forEach(v => {
      v.removeEventListener('error', this.onVideoError);
      v.removeEventListener('playing', this.onPlaying);
      v.removeEventListener('ended', this.onEnded);
    });
    if (this.$skipBtn) {
      this.$skipBtn.removeEventListener('click', this.boundSkipClick);
    }
    if (this.$replayBtn) {
      this.$replayBtn.removeEventListener('click', this.boundReplayClick);
    }
    if (this.$stopBtn) {
      this.$stopBtn.removeEventListener('click', this.boundStopClick);
    }
    if (this.$playBtn) {
      this.$playBtn.removeEventListener('click', this.boundPlayClick);
    }
    this.resetParallax();
    this.pauseAllVideos();
  }
}
